import {
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  NgZone,
  OnDestroy,
} from '@angular/core';

/** Animates the host element's `textContent` from the previously-shown number
    to the new one over `duration` ms, formatted by `format` each frame. The
    first value rendered is shown instantly (no animation on mount) — only
    subsequent value changes tween. */
@Directive({
  selector: '[softAnimatedNumber]',
  standalone: true,
})
export class AnimatedNumberDirective implements OnDestroy {
  readonly value = input.required<number | null | undefined>({
    alias: 'softAnimatedNumber',
  });
  readonly format = input.required<(value: number) => string>();
  readonly duration = input<number>(600);
  /** When false, render value changes instantly with no tween or
      direction-flash. Use for live tickers (clocks, timers) where the
      animation just feels like noise. */
  readonly animate = input<boolean>(true);

  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly zone = inject(NgZone);
  /** The numeric value the directive is logically settled on (or animating
      toward — `displayed` tracks the last painted intermediate). */
  private current = 0;
  /** The most recent value actually rendered to the DOM — used as the
      starting point of the next tween so a cancelled animation resumes from
      where it visually was, not where it logically started. */
  private displayed = 0;
  private rafId: number | null = null;
  private hasAnimatedInitial = false;

  constructor() {
    effect(() => {
      const next = this.value();
      const fmt = this.format();
      if (next === null || next === undefined || Number.isNaN(next)) {
        this.cancel();
        this.el.nativeElement.textContent = '';
        this.current = 0;
        this.displayed = 0;
        this.hasAnimatedInitial = false;
        return;
      }
      if (!this.hasAnimatedInitial) {
        if (next === 0) {
          this.current = 0;
          this.displayed = 0;
          this.el.nativeElement.textContent = fmt(0);
          return;
        }
        this.hasAnimatedInitial = true;
        this.displayed = 0;
        this.el.nativeElement.textContent = fmt(0);
        this.tween(0, next, fmt);
        return;
      }
      if (!this.animate()) {
        this.cancel();
        this.current = next;
        this.displayed = next;
        this.el.nativeElement.textContent = fmt(next);
        return;
      }
      // If a previous tween was mid-flight, start the new one from where it
      // was visually painted — prevents jumps when toggling fast.
      const startFrom = this.rafId !== null ? this.displayed : this.current;
      const direction = next > startFrom ? 'up' : next < startFrom ? 'down' : null;
      if (direction) this.flashDirection(direction);
      this.tween(startFrom, next, fmt);
    });
  }

  private flashDirection(direction: 'up' | 'down'): void {
    const node = this.el.nativeElement;
    node.classList.remove('ds-num-flash-up', 'ds-num-flash-down');
    void node.offsetWidth;
    node.classList.add(direction === 'up' ? 'ds-num-flash-up' : 'ds-num-flash-down');
  }

  ngOnDestroy(): void {
    this.cancel();
  }

  private tween(from: number, to: number, fmt: (v: number) => string): void {
    this.cancel();
    this.current = to;
    if (from === to) {
      this.displayed = to;
      this.el.nativeElement.textContent = fmt(to);
      return;
    }
    const duration = this.duration();
    const start = performance.now();
    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

    this.zone.runOutsideAngular(() => {
      const step = (now: number) => {
        const elapsed = now - start;
        const t = Math.min(1, elapsed / duration);
        const eased = easeOutCubic(t);
        const v = from + (to - from) * eased;
        this.displayed = v;
        this.el.nativeElement.textContent = fmt(v);
        if (t < 1) {
          this.rafId = requestAnimationFrame(step);
        } else {
          this.displayed = to;
          this.rafId = null;
        }
      };
      this.rafId = requestAnimationFrame(step);
    });
  }

  private cancel(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }
}
